import { lazy, Suspense, useEffect } from "react";
import { isRouteErrorResponse, useLocation, useRouteError } from "react-router";

import "@scss/routeErrorPage.scss";
import DelayedSpinner from "../../atoms/DelayedSpinner";

// Lazy so its stylesheet — which restyles every <a> — stays out of the entry
// CSS and only loads with the 404 page, as before.
const Display404NotFound = lazy(() => import("../../atoms/404NotFound"));

// Browsers word a failed dynamic import differently; all of these mean the
// chunk this tab was built against is gone, usually because of a deploy.
const STALE_CHUNK_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

const RELOAD_GUARD_KEY = "engage.routeError.reloadedFor";

export const isStaleChunkError = (error) =>
  STALE_CHUNK_PATTERN.test(String(error?.message ?? error ?? ""));

// Reload at most once per path, so a chunk that is genuinely missing shows the
// error page instead of looping.
const shouldReloadFor = (pathname) => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) !== pathname;
  } catch {
    return false;
  }
};

const reportError = (error) => {
  console.error("Route render failed", error);
  const reportable = error instanceof Error ? error : new Error(String(error));
  globalThis.newrelic?.noticeError?.(reportable);
};

// errorElement for the data router: replaces a white screen with a recoverable
// page when a route throws while loading or rendering.
export default function RouteErrorPage() {
  const error = useRouteError();
  const { pathname } = useLocation();
  const staleChunk = isStaleChunkError(error);
  const reloading = staleChunk && shouldReloadFor(pathname);
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (reloading) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, pathname);
      globalThis.location.reload();
      return;
    }
    if (!notFound) {
      reportError(error);
    }
  }, [error, notFound, pathname, reloading]);

  if (reloading) {
    return <DelayedSpinner />;
  }

  if (notFound) {
    return (
      <Suspense fallback={<DelayedSpinner />}>
        <Display404NotFound display />
      </Suspense>
    );
  }

  return (
    <div className="route-error" role="alert">
      <h1 className="route-error__title">Something went wrong</h1>
      <p className="route-error__message">
        {staleChunk
          ? "A newer version of Engage is available. Reload to continue."
          : "This page ran into a problem. Reloading usually fixes it."}
      </p>
      <div className="route-error__actions">
        <button
          type="button"
          className="route-error__button"
          onClick={() => globalThis.location.reload()}
        >
          Reload page
        </button>
        <a className="route-error__link" href="/home">
          Go to home
        </a>
      </div>
    </div>
  );
}
